// Debounced persistence of the user's UI language (en/fr).
//
// The EN/FR toggle updates i18next instantly for the UI, then calls
// queuePreferredLanguage(). Writes to the backend are debounced (trailing
// 2.5s) and skipped entirely when the settled value already matches what
// the server has — rapid flip-flopping never produces a DB call per click.
//
// Two persistence scopes share the same debounce pipeline:
//   'b2c' → borrowers.preferred_language  (consumers)
//   'b2b' → users.preferred_language      (officers/processors/admins)
//
// Plain module (no React). Module-level state survives across components.
import i18n from '../i18n';
import { authApi, getTenantSlugsFromHost } from '../services/stytchAuthService';

const ENDPOINTS = {
  b2c: '/b2c/user/preferred_language',
  b2b: '/auth/preferred_language',
};
const DEBOUNCE_MS = 2500;
const VALID = ['en', 'fr'];

// Last value known to be persisted server-side (seeded at login).
let lastPersisted = null;
// Latest queued (not yet persisted) value.
let pendingValue = null;
// Scope of the queued value ('b2c' | 'b2b'); last write wins.
let pendingScope = null;
// Trailing debounce timer.
let timer = null;
// One-time pagehide listener registration flag.
let pagehideBound = false;

const normalize = (lang) => {
  if (typeof lang !== 'string') return null;
  const value = lang.slice(0, 2).toLowerCase();
  return VALID.includes(value) ? value : null;
};

const flush = ({ keepalive } = {}) => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const value = pendingValue;
  const endpoint = ENDPOINTS[pendingScope];
  pendingValue = null;
  pendingScope = null;

  // No-spam guarantee: nothing queued, or the settled value already
  // matches what the server has — skip the call entirely.
  if (!value || !endpoint || value === lastPersisted) {
    return;
  }

  if (keepalive) {
    // Page is being hidden/unloaded — axios may be aborted, use fetch
    // with keepalive so the browser finishes the request. Fire-and-forget.
    try {
      const headers = { 'Content-Type': 'application/json' };
      const { clientSlug, branchSlug } = getTenantSlugsFromHost();
      if (clientSlug) headers['X-Tenant-Slug'] = clientSlug;
      if (branchSlug) headers['X-Branch-Slug'] = branchSlug;
      if (typeof window !== 'undefined' && window.location?.hostname) {
        headers['X-Tenant-Host'] = window.location.hostname;
      }
      fetch(`${authApi.defaults.baseURL}${endpoint}`, {
        method: 'PUT',
        keepalive: true,
        credentials: 'include',
        headers,
        body: JSON.stringify({ preferred_language: value }),
      }).catch(() => {});
      lastPersisted = value;
    } catch (error) {
      // Swallow - worst case it re-persists on a later toggle/login.
    }
    return;
  }

  authApi
    .put(endpoint, { preferred_language: value })
    .then(() => {
      lastPersisted = value;
    })
    .catch(() => {
      // Swallow errors - worst case it re-persists on a later toggle/login.
    });
};

const onPageHide = () => {
  flush({ keepalive: true });
};

/**
 * Apply the server-side language preference at login.
 * Seeds lastPersisted and switches i18next when it differs.
 */
export const applyServerPreferredLanguage = (lang) => {
  const value = normalize(lang);
  if (!value) return;

  lastPersisted = value;

  if (normalize(i18n.language) !== value) {
    i18n.changeLanguage(value);
  }
};

/**
 * Queue a language preference write (called on each toggle).
 * Debounced 2.5s trailing; flushed with keepalive on pagehide.
 *
 * @param {string} lang  'en' | 'fr'
 * @param {string} scope 'b2c' (consumers) | 'b2b' (officers/processors/admins)
 */
export const queuePreferredLanguage = (lang, scope = 'b2c') => {
  const value = normalize(lang);
  if (!value || !ENDPOINTS[scope]) return;

  pendingValue = value;
  pendingScope = scope;

  if (!pagehideBound && typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
    pagehideBound = true;
  }

  if (timer) clearTimeout(timer);
  timer = setTimeout(() => flush(), DEBOUNCE_MS);
};
